import { Delete, Liquor } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Cafe, OrderItem, OrderItemStatus as OrderItemStatusType } from '../../../apiTypes';
import EditModal from '../../../components/EditModal';
import FormButtonSelect from '../../../components/forms/FomsButtonSelect';
import FormArrayField from '../../../components/forms/FormArrayField';
import FormSelect from '../../../components/forms/FormSelect';
import FormTextField from '../../../components/forms/FormTextField';
import KeyBoard from '../../../components/KeyBoard';
import OrderItemStatus from '../../../components/OrderItemStatus';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import { useLocale } from '../../../contexts/locale.context';

const getStatuses = (cafe?: DbDocument<Cafe> | null): OrderItemStatusType[] => {
  if (cafe?.orderStatusesSetting === 'short') {
    return ['ordered', 'complete'];
  }

  return ['ordered', 'processing', 'ready', 'complete'];
};

type Props = {
  open: boolean;
  item: OrderItem;
  onClose: () => void;
  onChange: (item: OrderItem) => void;
  onRemove: (item: OrderItem) => void;
  canRemove: boolean;
  canEditWight: boolean;
  fiscalized?: boolean;
};

const EditMods = ({ item, open, onClose, onChange, onRemove, canRemove, canEditWight, fiscalized }: Props) => {
  const { lang } = useLocale();
  const { dishesMap, isMultyUser } = useCafe();
  const { cafe } = useAdmin();

  const dish = dishesMap[item.dishId];
  const dishVariations = dish?.variations;
  const dishModifications = dish?.modifications;

  const form = useForm<OrderItem>({
    mode: 'onChange',
    defaultValues: item,
  });

  const { getValues } = form;
  const weight = form.watch('weight');
  const excises = form.watch('excises');
  const variation = form.watch('variation');
  const itemModifications = form.watch('modifications');

  const statuses = useMemo(() => getStatuses(cafe), [cafe]);

  const handleClose = () => {
    onClose();
    form.reset();
  };

  const saveChanges = () => {
    onChange && onChange(getValues());
    handleClose();
  };

  return (
    <EditModal<OrderItem>
      title={dish?.name || ''}
      imgUrl={dish?.imageUrl}
      open={open}
      onClose={handleClose}
      submitButton={t.save_changes[lang]}
      onSubmit={saveChanges}
      submitButtonProps={{ disabled: !form.formState.isDirty }}
      secondaryButton={t.remove[lang]}
      secondaryButtonProps={{ startIcon: <Delete />, disabled: !canRemove }}
      onSecondaryButtonClick={() => {
        onRemove(item);
        handleClose();
      }}
      form={form}
      size="xs"
      stopPropagation={false}
    >
      <Box>
        {cafe?.orderStatusesSetting !== 'off' && (
          <FormSelect
            name="status"
            options={statuses}
            label={t.status[lang]}
            renderOption={(status) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <OrderItemStatus status={status} />
                <Typography>{t[`status_${status}`][lang]}</Typography>
              </Box>
            )}
            sx={{ mb: 3 }}
          />
        )}

        {item.byWeight && (
          <>
            <FormTextField
              name="weight"
              type="number"
              label={t.portion[lang]}
              fullWidth
              disabled={!canEditWight}
              rules={{ required: true, min: 1, pattern: /^[0-9]{0,5}$/ }}
              InputProps={{
                endAdornment: <InputAdornment position="end">{t[item.unit || 'g'][lang]}</InputAdornment>,
              }}
              sx={{ mb: 2 }}
            />

            {isMultyUser && (
              <KeyBoard
                value={weight || ''}
                onChange={(v) => form.setValue('weight', v, { shouldDirty: true })}
                size="small"
              />
            )}
          </>
        )}

        <Box sx={{ mb: 3 }}>
          <FormButtonSelect name="queue" label={t.queue[lang]} options={['1', '2', '3']} />
        </Box>

        {dishVariations && dishVariations.length > 0 && (
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="variation">{t.variation[lang]}</InputLabel>
            <Select
              id="variation"
              value={dishVariations.findIndex((i) => i.name === variation?.name)}
              label={t.variation[lang]}
              fullWidth
              onChange={(e) =>
                form.setValue(
                  'variation',
                  typeof e.target.value === 'number' ? dishVariations[e.target.value] : dishVariations[0],
                  { shouldDirty: true }
                )
              }
            >
              {dishVariations.map((variation, index) => (
                <MenuItem key={variation.name} value={index}>
                  {variation.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {dishModifications && dishModifications.length > 0 && (
          <>
            <Typography color={(t) => t.palette.text.secondary}>{t.modifications[lang]}</Typography>
            {dishModifications.map((dishMod, index) => (
              <FormControlLabel
                key={dishMod.name}
                label={dishMod.name}
                disabled={!canRemove}
                control={
                  <Checkbox
                    color="secondary"
                    checked={Boolean(itemModifications?.find((m) => m.name === dishMod.name))}
                    onChange={(e, checked) => {
                      if (checked) {
                        form.setValue('modifications', [...(itemModifications || []), dishMod], { shouldDirty: true });
                      } else {
                        form.setValue(
                          'modifications',
                          (itemModifications || []).filter((m) => m.name !== dishMod.name),
                          { shouldDirty: true }
                        );
                      }
                    }}
                  />
                }
              />
            ))}
          </>
        )}

        <FormTextField sx={{ mb: 3 }} multiline fullWidth name="comment" label={t.comment[lang]} />

        {dish?.taxInfo?.hasExcise && (
          <Box>
            <Typography sx={{ display: 'flex', gap: 1, alignItems: 'center' }} color={(t) => t.palette.text.secondary}>
              <Liquor /> Акцизні макри щойно відкритих пляшок:
            </Typography>
            <FormArrayField
              disabled={fiscalized}
              name="excises"
              label="Код акцизної макрки"
              addButtonLabel="відкрити пляшку"
            />
          </Box>
        )}
      </Box>
    </EditModal>
  );
};

export default EditMods;
